import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Column, useTable } from "react-table";

import { SortDirection } from "../../enums";
import { TableHeaderCell } from "./TableHeaderCell";
import { TableRowCell } from "./TableRowCell";
import { TableRowCellData } from "./TableRowCellData.type";
import { TableColumn } from "./TableColumn.type";

import s from "./Table.module.scss";
import { useWindowSize } from "../../utils/useWindowSize";

import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css'

interface IProps<T extends object> {
	className?: string;
	data: T[];
	columns: TableColumn<T>[];
	onClickRow?: (item: T) => void;
	rowStyle?: (item: T) => string;
}

export const Table = <T extends object>({ className, data, columns, onClickRow, rowStyle }: IProps<T>): JSX.Element => {
	const { width } = useWindowSize();
	const [sortField, setSortField] = useState<Extract<keyof T, string>>();
	const [sortDirection, setSortDirection] = useState<SortDirection>();
	const [isShowHeaderShadow, setShowHeaderShadow] = useState<boolean>(false);
	const [isShowFooterShadow, setShowFooterShadow] = useState<boolean>(false);
	const [showTooltip, setShowTooltip] = useState(false)
	const [tableWidth, setTableWidth] = useState<number>(0);

	const containerRef = useRef<HTMLDivElement>(null);

	const tColumns = useMemo<Column<T>[]>(() => columns.map(column => (
		{
			accessor: column.accessor,
			Header: (
				<TableHeaderCell<T>
					accessor={column.accessor}
					sortable={column.sortable}
					filterable={column.filterable}
					title={column.title}
					titleAlign={column.titleAlign}
					sort={sortField === column.accessor ? sortDirection : undefined}
					onChangeSortDirection={(accessor, _sortDirection) => {
						setSortField(accessor);
						setSortDirection(_sortDirection);
					}}
				/>
			),
			Cell: column.Cell ?? ((c: TableRowCellData<T>) => (<div>{c.value}</div>)),
			width: column.width,
			maxWidth: column.maxWidth,
			minWidth: column.minWidth,
			widthUnits: column.widthUnits,
		}
	)), [columns, sortDirection, sortField]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		rows,
	} = useTable(
		{
			columns: tColumns,
			data,
		},
	);

	const onScrollHandler = useCallback((event: React.UIEvent<HTMLDivElement>) => {
		setShowHeaderShadow(event.currentTarget.scrollTop >= 1);
		setShowFooterShadow(event.currentTarget.scrollHeight - event.currentTarget.offsetHeight - event.currentTarget.scrollTop >= 1);
	}, []);

	const mouseEnterHandler = useCallback(
		(e: React.UIEvent<HTMLDivElement>) => {
			let target = e.target
			while (
				target.children &&
				target.children.length &&
				target.children[0].tagName === 'DIV'
				) {
				target = target.firstChild
			}
			const c = target.cloneNode(true)
			c.style.display = 'inline'
			c.style.width = 'auto'
			c.style.visibility = 'hidden'
			target.parentElement.appendChild(c)
			let truncated = false
			try {
				const displayWidth = target.getBoundingClientRect().width
				const fullWidth = c.getBoundingClientRect().width
				// console.log(fullWidth, displayWidth)
				truncated = displayWidth < fullWidth
			} catch (ex) {
				// console.log('catch')
			}

			//
			c.remove()
			// console.log(target, target.offsetWidth, target.scrollWidth, truncated)
			if (truncated && !showTooltip) {
				setShowTooltip(true)
			} else if (!truncated && showTooltip) {
				setShowTooltip(false)
			}
		},
		[setShowTooltip]
	)

	const mouseLeaveHandler = useCallback(() => {
		setShowTooltip(false);
	}, [setShowTooltip])


	useEffect(() => {
		if (containerRef.current) {
			setShowHeaderShadow(containerRef.current.scrollTop >= 1);
			setShowFooterShadow(containerRef.current.scrollHeight - containerRef.current.offsetHeight - containerRef.current.scrollTop >= 1);
		}
	}, [data]);

	useEffect(() => {
		if (!containerRef.current) {
			return;
		}
		setTableWidth(containerRef.current.offsetWidth)
	},[width, containerRef.current?.offsetWidth])

	return (
		<div className={`${s.container} ${className ?? ""}`} onScroll={onScrollHandler} ref={containerRef}>
			<table {...getTableProps()} className={s.table}>
				<thead className={isShowHeaderShadow ? s.table__headerShadow : s.table__normalHeader}>
					{headerGroups.map(headerGroup => (
						<tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.getHeaderGroupProps().key}>
							{headerGroup.headers.map(column => (
								<th {...column.getHeaderProps({
									style: {
										minWidth: column.minWidth ? (column.widthUnits === '%' ? `${column.minWidth}%` : `${column.minWidth * ((width ?? 1000) <= 800 ? 1 : 1.2)}px`) : undefined,
										maxWidth: column.maxWidth ? (column.widthUnits === '%' ? `${column.maxWidth}%` : `${column.maxWidth * ((width ?? 1000) <= 800 ? 1 : 1.2)}px`) : undefined,
										width: column.width ? (column.widthUnits === '%' ? `${tableWidth * (+column.width) / 100}px` : `${+column.width * ((width ?? 1000) <= 800 ? 1 : 1.2)}px`) : undefined
									},
								})} key={column.getHeaderProps().key}>
									{column.render("Header")}
								</th>
							))}
						</tr>
					))}
				</thead>

				<tbody {...getTableBodyProps()}>
					{rows.length !== 0 && rows.map(row => {
						prepareRow(row);

						const onClickHandler = (): void => {
							if (onClickRow) {
								onClickRow(row.original);
							}
						};
						return (
							<tr
								{...row.getRowProps()}
								key={row.getRowProps().key}
								onClick={onClickHandler}
								className={`${rowStyle ? rowStyle(row.original) : ""}`}
							>
								{
									row.cells.map(cell => (
										<td {...cell.getCellProps({
											style: {

												minWidth: cell.column.minWidth ? (cell.column.widthUnits === '%' ? `${cell.column.minWidth}%` : `${cell.column.minWidth * ((width ?? 1000) <= 800 ? 1 : 1.2)}px`) : undefined,
												maxWidth: cell.column.maxWidth ? (cell.column.widthUnits === '%' ? `${cell.column.maxWidth}%` : `${cell.column.maxWidth * ((width ?? 1000) <= 800 ? 1 : 1.2)}px`) : undefined,
												width: cell.column.width ? (cell.column.widthUnits === '%' ? `${tableWidth * (+cell.column.width) / 100}px` : `${+cell.column.width * ((width ?? 1000) <= 800 ? 1 : 1.2)}px`) : undefined
											},
										})} key={cell.getCellProps().key}
											data-tooltip-id='table-row-tooltip'
											data-tooltip-content={cell.value}
											onMouseEnter={mouseEnterHandler}
											onMouseLeave={mouseLeaveHandler}>
											<TableRowCell cellStyle={{
												maxWidth: cell.column.maxWidth && cell.column.widthUnits === '%' ? `${tableWidth*cell.column.maxWidth/100}px` : undefined,
												minWidth: cell.column.minWidth && cell.column.widthUnits === '%' ? `${tableWidth*cell.column.minWidth/100}px` : undefined
											}}>
												{cell.render("Cell")}
											</TableRowCell>
										</td>
									))
								}
							</tr>
						);
					})}
				</tbody>
				{/* <tfoot className={isShowFooterShadow ? s.table__footerShadow : s.table__normalFooter}>*/}
				{/* <tr>*/}
				{/* <td colSpan={columns.length} >-</td>*/}
				{/* </tr>*/}
				{/* </tfoot>*/}
			</table>
			<Tooltip id="table-row-tooltip" place="bottom" isOpen={showTooltip} style={{zIndex: 2}}/>
			<div className={`${s.container__footer} ${isShowFooterShadow ? s.container__shadowFooter : s.container__normalFooter}`} />
		</div>
	);
};
